import axios, { AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import { API_ROOT } from './config';

axios.defaults.withCredentials = true;

const downloadFile = <T>(response: AxiosResponse<T>): void => {
  const blob = new Blob([response.data as unknown as BlobPart]);
  const url = window.URL.createObjectURL(blob);

  const contentDisposition = response.headers['content-disposition'];
  const match =
    contentDisposition && contentDisposition.match(/filename="(.+)"/);
  const fileName = match ? match[1] : 'download'; // Use filename from header, or fallback to 'download'

  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
};

const refetchAuthToken = async (rfToken: string): Promise<void> => {
  try {
    const response: AxiosResponse = await axios({
      url: `${API_ROOT}/refresh-token`,
      method: 'get',
      headers: {
        grant_type: 'refresh-token',
        Authorization: `Bearer ${rfToken}`,
      },
    });
    window.localStorage.setItem('authToken', response.data.jwt_token);
  } catch (error: any) {
    window.location.href = '/freshid/logout';
  }
};

const request = async <T = any>(
  url: string,
  payload?: Record<string, any> | FormData,
  method?: Method,
  headers?: AxiosRequestConfig['headers'],
  responseType?: AxiosRequestConfig['responseType'],
): Promise<T> => {
  const endpoint = `${API_ROOT}${url}`;
  const authToken = window.localStorage.getItem('authToken');
  try {
    const response: AxiosResponse<T> = await axios({
      data: payload,
      url: endpoint,
      method: method || 'get',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
        ...(headers || {}),
      },
      responseType,
    });

    if (['blob', 'arraybuffer'].includes(responseType || '')) {
      downloadFile(response);
    }

    return response.data;
  } catch (error: any) {
    if (error.response) {
      const { status, data } = error.response;
      if (status === 401) {
        window.location.href = '/freshid/logout';
      } else if (status === 403) {
        const rf = localStorage.getItem('rfToken') || '';
        switch (data.error_message || data) {
          case 'freshsurvey_invalid_accountuser':
          case 'freshsurvey_invalid_accountinvite':
            localStorage.clear();
            window.location.href = `${window?.location?.origin}/no-account`;
            break;
          case 'Session Expired':
            if (rf) {
              refetchAuthToken(rf);
            } else {
              window.location.href = '/freshid/logout';
            }
            break;
          default:
            throw error;
        }
      } else if (status >= 400) {
        if (data && data === 'Account invited expired.') {
          localStorage.clear();
          window.location.href = `${window?.location?.origin}/invite-expired`;
        }
        throw error;
      }
    } else if (error.request) {
      throw new Error('Network error');
      // network problem or a gateway issue where there is no response received
    }

    return undefined as unknown as T;
  }
};

export default request;
